import React from "react";
import { Users, Trash2 } from "lucide-react";

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  newGroup: {
    name: string;
    description: string;
    recipients: any[];
  };
  setNewGroup: React.Dispatch<
    React.SetStateAction<{
      name: string;
      description: string;
      recipients: any[];
    }>
  >;
  selectedRecipients: any[];
  onRemoveRecipient: (recipientId: string) => void;
  onSelectRecipients: () => void;
  onAddGroup: () => void;
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  newGroup,
  setNewGroup,
  selectedRecipients,
  onRemoveRecipient,
  onSelectRecipients,
  onAddGroup,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900">
            Create New Group
          </h3>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Name *
              </label>
              <input
                type="text"
                value={newGroup.name}
                onChange={(e) =>
                  setNewGroup({ ...newGroup, name: e.target.value })
                }
                placeholder="Enter group name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={newGroup.description}
                onChange={(e) =>
                  setNewGroup({ ...newGroup, description: e.target.value })
                }
                placeholder="Enter group description..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipients ({selectedRecipients.length})
              </label>
              <div className="border border-gray-300 rounded-lg p-3 min-h-[100px] max-h-[300px] overflow-y-auto">
                {selectedRecipients.length > 0 ? (
                  <div className="space-y-2">
                    {selectedRecipients.map((recipient) => (
                      <div
                        key={recipient.id}
                        className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900 block truncate">
                            {recipient.name}
                          </span>
                          <span className="text-sm text-gray-500 truncate block">
                            {recipient.email}
                          </span>
                        </div>
                        <button
                          onClick={() => onRemoveRecipient(recipient.id)}
                          className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No recipients selected</p>
                  </div>
                )}
              </div>
              <button
                onClick={onSelectRecipients}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                + Add Recipients
              </button>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onAddGroup}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Group
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;
